import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.classList.contains('media-gallery--video')) {
      this.addEventListener(SlideshowSelectEvent.eventName, this.#pauseInactiveVideos, { signal });
    }

    if (this.classList.contains('media-gallery--image')) {
      this.#initProductBadgeVisibility(signal);
    }
  }

  /**
   * Shows product badge/label only on the first N image slides.
   * @param {AbortSignal} signal
   */
  #initProductBadgeVisibility(signal) {
    const badgeLayer = this.parentElement?.querySelector('.main-product-badge-layer');
    if (!badgeLayer) return;

    const maxVisible = parseInt(
      this.dataset.badgeVisibleSlides || badgeLayer.dataset.visibleSlides || '3',
      10
    );

    const updateBadgeVisibility = (index) => {
      badgeLayer.hidden = index >= maxVisible;
    };

    const handleSlideSelect = (event) => {
      updateBadgeVisibility(event.detail.index);
    };

    this.addEventListener(SlideshowSelectEvent.eventName, handleSlideSelect, { signal });

    const slideshow = this.refs.slideshow;
    if (slideshow) {
      updateBadgeVisibility(slideshow.current ?? 0);
    }
  }

  /**
   * Pauses videos on slides that are no longer active.
   * @param {SlideshowSelectEvent} event
   */
  #pauseInactiveVideos = (event) => {
    const { slide } = event.detail;

    this.querySelectorAll('slideshow-slide deferred-media').forEach((media) => {
      const slideElement = media.closest('slideshow-slide');
      if (!slideElement || slideElement === slide) return;
      if (typeof media.pauseMedia === 'function') {
        media.pauseMedia();
      }
    });
  };

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
